// SearchLogic.js
import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../AppContext";

// Contains the logic for the search bar
// Component for a search bar is passed in as children so that differently styled search bars can be used
function SearchLogic({ children }) {
    const {
        setSearchResultsManuals,
        snToManuals,
        setSearchResultsBF,
        queryToBF,
        snToPartsRC,
        snToPartsSNL,
        setSearchResultsPartsRC,
        setSearchResultsPartsSNL,
        setIsLoading,
        isLoading,
        setKeepSRBlank,
        logSearchHandler,
        logResultsHandler,
        setSearchId,
        setFeedbackPanelStatesRC,
        setFeedbackPanelStatesSNL,
        setFeedbackPanelStatesWeb,
        setSearchResultsPartsWebsite,
        snToPartsWebsite,
        serNum,
        setSerNum,
        modNum,
        setModNum,
        manName,
        setManName,
        refineQuery,
        setRefineQuery,
        setSearchResultsManualsTraceback,
        setSearchResultsBFTraceback,
        setSearchResultsPartsRCTraceback,
        setSearchResultsPartsSNLTraceback,
        setSearchResultsPartsWebsiteTraceback,
        setSearchResultsPartsParsed,
        snToPartsParsed,
        setSearchResultsPartsTraceback,
        setFeedbackPanelStatesParsed,
        setSearchResultsPartsAll,
        setSearchResultsPartsAllTraceback,
        snToPartsAll,
        setIsConsolidatedLoading,
        setActiveSearchTermFilter,
    } = useContext(AppContext);

    const [localSerNum, setLocalSerNum] = useState(serNum);
    const [localModNum, setLocalModNum] = useState(modNum);
    const [localRefineQuery, setLocalRefineQuery] = useState(refineQuery);
    const [localManName, setLocalManName] = useState(manName);

    const [submissionError, setSubmissionError] = useState(""); // Prints if both serial number and model number are blank/whitespace

    const navigate = useNavigate();

    //Calls the search APIS
    const doTheSearch = async () => {
        //Resets the results before search begins
        setSearchResultsManuals({});
        setSearchResultsBF({});
        setSearchResultsPartsRC({});
        setSearchResultsPartsSNL({});
        setSearchResultsPartsWebsite({});
        setSearchResultsPartsParsed({});
        setSearchResultsPartsAll({});

        // Resets traceback
        setSearchResultsManualsTraceback({});
        setSearchResultsBFTraceback({});
        setSearchResultsPartsRCTraceback({});
        setSearchResultsPartsSNLTraceback({});
        setSearchResultsPartsWebsiteTraceback({});
        setSearchResultsPartsTraceback({});
        setSearchResultsPartsAllTraceback({});
        setIsConsolidatedLoading(false);

        // Resets feedback panel each search
        setFeedbackPanelStatesRC({});
        setFeedbackPanelStatesSNL({});
        setFeedbackPanelStatesWeb({});
        setFeedbackPanelStatesParsed({});
        setActiveSearchTermFilter("all");

        //Tells isLoading that there are X calls about to be made
        //The API calls decrement from isLoading but the calling function should add them
        //Prevents race condition where one function adds 1 and removes 1 while the other function hasn't yet added
        setIsLoading((prev) => prev + 6);

        // Redirect the app to the search page
        const searchParams = [
            `man=${encodeURIComponent(localManName)}`,
            `sn=${encodeURIComponent(localSerNum)}`,
            `mn=${encodeURIComponent(localModNum)}`,
            `refine=${encodeURIComponent(localRefineQuery)}`,
        ]
            .filter(Boolean)
            .join("/");
        navigate(`/results/${searchParams}`);

        try {
            //Logs the search, gets the search ID and sets the search ID state variable
            const newSearchId = await logSearchHandler(
                localManName,
                localModNum,
                localSerNum,
                localRefineQuery
            );

            setSearchId(newSearchId);

            // 1/4 of the way through the search
            setIsLoading((prev) => prev - 1);

            // Now we run each search endpoint asynchronously.
            // We have to wait for them to each return a promise
            // to indicate they've finished (or errored out) and settled
            // before moving on to log results
            const results = await Promise.allSettled([
                //Searches up manuals and parts based on inputted serial number and/or model number
                snToManuals(localSerNum, localModNum, localManName),
                //snToParts(localSerNum, localModNum, localManName, localRefineQuery);
                //queryToBF(localRefineQuery);
                snToPartsRC(
                    localSerNum,
                    localModNum,
                    localManName,
                    localRefineQuery
                ),
                snToPartsSNL(
                    localSerNum,
                    localModNum,
                    localManName,
                    localRefineQuery
                ),
                snToPartsWebsite(
                    localSerNum,
                    localModNum,
                    localManName,
                    localRefineQuery
                ),
                snToPartsParsed(
                    localSerNum,
                    localModNum,
                    localManName,
                    localRefineQuery
                ),
            ]);

            // Extract the results from the promises
            console.log("results!!!!!!!!!!!!", results);
            const [newManRes, newRCRes, newSNLRes, newWebRes, newParsedRes] =
                results.map((result) => {
                    if (result.status === "fulfilled" && result.value?.isError === true) {
                        console.log("result.isError", result.value.isError);
                        console.log("result.parts", result.value.parts);
                        console.log(
                            "result.errorMessage",
                            result.value.errorMessage
                        );
                        // return result.value;
                    }

                    return result.status === "fulfilled"
                        ? result.value?.casesList
                            ? result.value
                            : result.value.parts
                        : result.reason;
                });
            const [
                newManResTraceback,
                newRCResTraceback,
                newSNLResTraceback,
                newWebResTraceback,
                newParsedResTraceback,
            ] = results.map((result) => {
                return result.status === "fulfilled"
                    ? {
                          isError: result.value.isError ? true : false,
                          errorMessage: result.value.errorMessage
                              ? result.value.errorMessage
                              : "Error message is not set or not implemented",
                          emptyTraceback: result.value.emptyTraceback
                              ? result.value.emptyTraceback
                              : "", // Traceback defaults to empty string for now
                      }
                    : result.reason;
            });
            // Sets the state variables for the search results
            // TODO: modify the return format here
            setSearchResultsManuals(newManRes);
            setSearchResultsPartsRC(newRCRes);
            setSearchResultsPartsSNL(newSNLRes);
            setSearchResultsPartsWebsite(newWebRes);
            setSearchResultsPartsParsed(newParsedRes);

            setSearchResultsManualsTraceback(newManResTraceback);
            setSearchResultsPartsRCTraceback(newRCResTraceback);
            setSearchResultsPartsSNLTraceback(newSNLResTraceback);
            setSearchResultsPartsWebsiteTraceback(newWebResTraceback);
            setSearchResultsPartsTraceback(newParsedResTraceback);

            console.log("newManRes", newManRes);
            console.log("newRCRes", newRCRes);
            console.log("newSNLRes", newSNLRes);
            console.log("newWebRes", newWebRes);
            console.log("newParsedRes", newParsedRes);

            setIsConsolidatedLoading(true);
            snToPartsAll(
                localSerNum,
                localModNum,
                localManName,
                localRefineQuery
            ).then((consolidatedRes) => {
                const resolved = consolidatedRes ?? {};
                setSearchResultsPartsAll(resolved);
                setSearchResultsPartsAllTraceback({
                    isError: resolved?.isError ?? false,
                    errorMessage: resolved?.errorMessage ?? "",
                    emptyTraceback: resolved?.emptyTraceback ?? "",
                });
                const consolidatedPayload = {
                    parts: Array.isArray(resolved?.parts) ? resolved.parts : [],
                };

                logResultsHandler(
                    newSearchId,
                    newRCRes?.casesList,
                    newRCRes?.parts,
                    newSNLRes,
                    newWebRes,
                    newParsedRes,
                    newManRes,
                    consolidatedPayload
                );
            }).catch((error) => {
                console.error("Consolidated search failed:", error);
                const fallbackResolved = {
                    parts: [],
                    isError: true,
                    errorMessage: error?.message ?? "Unable to load consolidated results.",
                    emptyTraceback: "",
                };
                setSearchResultsPartsAll(fallbackResolved);
                setSearchResultsPartsAllTraceback({
                    isError: true,
                    errorMessage: error?.message ?? "Unable to load consolidated results.",
                    emptyTraceback: "",
                });
                logResultsHandler(
                    newSearchId,
                    newRCRes?.casesList,
                    newRCRes?.parts,
                    newSNLRes,
                    newWebRes,
                    newParsedRes,
                    newManRes,
                    { parts: [] }
                );
            }).finally(() => {
                setIsConsolidatedLoading(false);
            });
		} catch (error) {
			console.log("An error occurred while searching:", error);
			setIsLoading((prev) => prev - 1);
		} finally {
			//Tells isLoading that there are X less calls to be made
			setIsLoading((prev) => prev - 5);
        }
    };

    //Activates the function to input serial number and get back manuals
	const handleSearch = async (e = null) => {
		if (e) {
			e.preventDefault();
		}

        // Sets the context state variables to the current values of the input fields
        setSerNum(localSerNum);
        setModNum(localModNum);
        setManName(localManName);
        setRefineQuery(localRefineQuery);

        //Doesn't search if both serial number and model number are blank/whitespace
        //Also doesn't search if there is already an API call in progress
        // or if there is a submission error
        // TODO (Joan): check the 'E' serial number condition after discussing with team
		if (
            localManName.replace(/\s+/g, "") === "" ||
            (localSerNum.replace(/\s+/g, "") === "" &&
                localModNum.replace(/\s+/g, "") === "") ||
            isLoading > 0 ||
            submissionError !== ""
        ) {
            if (localManName.replace(/\s+/g, "") === "") {
                setSubmissionError("Please select a manufacturer.");
            }
            // Sets error message if both serial number and model number are blank/whitespace
            else if (
                localSerNum.replace(/\s+/g, "") === "" &&
                localModNum.replace(/\s+/g, "") === ""
            ) {
                setSubmissionError(
                    "Please enter a value for Serial Number and/or Model."
                );
            }
            return;
        } else {
            setSubmissionError("");
        }

        //Search results should show up whether it's cards or "No results"
        setKeepSRBlank(false);

        //Calls the manuals and parts API endpoints and logs them
        doTheSearch();
	};

    //Updates change to input bar for serial and model number
    const handleInputChangeWithCursor = (setLocalValFunc) => (e) => {
        const { selectionStart, selectionEnd, value } = e.target;
        const upperValue = value.toUpperCase();
        setLocalValFunc(upperValue);
        // Preserve cursor position after updating state
        requestAnimationFrame(() => {
            e.target.setSelectionRange(selectionStart, selectionEnd);
        });
    };

    //Updates change to input bar for refine query
    const handleSearchRefineQueryChange = (e) => {
        setLocalRefineQuery(e.target.value);
    };

    // Business logic for the search bar if serial number starts with the letter "E"
    // and manufacturer is "Norlake" or "Master-Bilt"
    useEffect(() => {
        // if (localSerNum.startsWith('E') && (localManName === 'Norlake' || localManName === 'Master-Bilt')) {
        // 	setSubmissionError("Serial numbers starting with 'E' are not supported for Norlake or Master-Bilt. Please create a PartsID ticket instead.");
        // } else {
        // 	setSubmissionError("");
        // }
        setSubmissionError("");
    }, [localSerNum, localModNum, localManName]);

    //Returns rendered version of whatever search bar is passed in as children
    return children({
        serNum: localSerNum,
        modNum: localModNum,
        manName: localManName,
        refineQuery: localRefineQuery,
        handleSearchSNChange: handleInputChangeWithCursor(setLocalSerNum),
        handleSearchMNChange: handleInputChangeWithCursor(setLocalModNum),
        handleSearchRefineQueryChange,
        setManName: setLocalManName,
        setSerNum: setLocalSerNum,
        setModNum: setLocalModNum,
        setRefineQuery: setLocalRefineQuery,
        handleSearch,
        submissionError,
    });
}

export default SearchLogic;
